/**
 * §6.1 — THE 100% RULE, on the client side. **This file is now a re-export, and that is the point
 * of it.**
 *
 * ── WHAT CHANGED ───────────────────────────────────────────────────────────────────────
 *
 * The arithmetic used to be transcribed here. It was written that way deliberately and the header
 * this replaces said why — D-009's package did not exist yet, and the live running total is the
 * whole of CAP-05 — and it named the exit: *"When A3's package lands this file becomes a re-export
 * of it and the only thing that changes is the import."*
 *
 * `@platform/erp-scoring` now exists, so the import is the only thing that changed. The server's
 * own copy went the same way in the same pass
 * (`apps/portal/server/src/erp_consolidation/weights/balance.ts`, now a re-export of the same
 * module), so **the rule stops existing three times and starts existing once**:
 *
 *   > D-009: *"This is what makes the 100% rule enforceable identically on both sides — §6.1
 *   > requires the client to show a live running total and the server to refuse a baseline, and a
 *   > rule implemented twice is a rule that will eventually disagree with itself."*
 *
 * A faithful transcription does not satisfy that ruling. It is correct today and diverges the
 * first time somebody fixes a rounding edge in one copy and not the other. Being the same code
 * does satisfy it, and `formatBalanceMessage` having one definition is why the sentence on screen
 * while somebody types is byte-identical to the sentence in the 409 they get if they baseline
 * anyway — and the one on the refusal is the one an auditor reads.
 *
 * `@platform/*` is the platform's own shared layer and is imported normally, exactly as
 * `@platform/ui-kit` is (SCREEN_MAP's box: D-003 is about APR's **product folders**). This is not
 * a cross-product import.
 *
 * ── WHY THE FILE STILL EXISTS RATHER THAN BEING DELETED ────────────────────────────────
 *
 * Four modules in this folder import from `../weights` — the capability model editor, the module's
 * own `mock/mockServer.ts`, and (as types) its screens. Rewriting their imports would be an edit
 * to files that have no other reason to change in this pass. The re-export is what the old header
 * asked for, and a call site is free to import `@platform/erp-scoring` directly whenever it is
 * next open for another reason.
 *
 * **Everything the package offers under these names is re-exported, including what nothing here
 * imports yet.** Narrowing the surface while moving it would be two changes dressed as one
 * (CLAUDE.md, additive change only).
 *
 * ── THE THREE BEHAVIOURS THAT ARE THE PACKAGE'S AND NOT THE OLD TRANSCRIPTION'S ────────
 *
 * Found by reading it rather than assuming, because each one reaches a call site:
 *
 *  1. **`distributeEvenly` and `normalizeProportionally` return ONE ENTRY PER SIBLING**, locked
 *     ones carrying the weight they already had — §3.2's *"a full replacement set summing to
 *     exactly 1000"*. The transcription returned only the unlocked rows. The package is right, and
 *     a locked row sent with the value it already has is not a change, so §6.17's `weight_locked`
 *     refusal is not triggered by it.
 *  2. **With nothing unlocked they return the set UNCHANGED, not an empty array.** So "is there
 *     anything to distribute?" is answered by `weightCapacity`, not by the length of the result —
 *     which is what that function exists for: *"A client needs to disable a button, not catch an
 *     exception."*
 *  3. **`WeightHelperError`** replaces the transcription's own error class, and carries the
 *     snake_case `code` the wire uses (`locked_weights_exceed_100`), so a screen and a route name
 *     the same condition the same way.
 *
 * Verified by running the three cases proved before the move, through the package: 7 unlocked →
 * `148,142,142,142,142,142,142` = 1000; 3 unlocked with 30.0% locked → `234,233,233` + 300 = 1000;
 * normalize 33.3×3 → `334,333,333` = 1000. Unchanged, so nothing had diverged.
 */

export {
  /* §6.1 — the balance, the two sentences, and the tree walk. */
  ONE_HUNDRED_PCT_TENTHS,
  checkBalance,
  describeBalance,
  formatBalanceMessage,
  summariseBalance,
  orderTree,
  /* §3.2 — the two helpers, the capacity test that gates them, and their refusal. */
  distributeEvenly,
  normalizeProportionally,
  weightCapacity,
  WeightHelperError,
  /* The rest of the tree surface, re-exported rather than narrowed on the way across. */
  childCounts,
  depths,
  effectiveWeights,
  subtreeIds,
  wouldCycle,
} from '@platform/erp-scoring';

export type {
  BalanceResult,
  WeightBalance,
  WeightCapacity,
  WeightChange,
  WeightGroup,
  WeightNode,
  WeightSibling,
} from '@platform/erp-scoring';

/* ──────────────────────────────────────────────────────────────────────────────────────
   The two things that are NOT the package's, because they are presentation.

   The package is pure arithmetic over integers (`units.ts`: no clock, no DOM, no formatting of an
   input unit). Reading a percentage out of a text input and writing one back into it is this
   module's business, so it stays here — and it is the only arithmetic-adjacent code left in this
   file.
   ────────────────────────────────────────────────────────────────────────────────────── */

/**
 * A weight in TENTHS, as a field shows it: `1000` reads `"100.0"`.
 *
 * NOT `formatPct` from the package, and the distinction is the unit system's, not a preference:
 * `formatPct` renders a COMPUTED percentage, which `units.ts` carries in ten-thousandths. A weight
 * is an INPUT and is carried in tenths, so putting it through `formatPct` would be out by a factor
 * of a thousand. One decimal place, because §6.1 is explicit that weights are entered to one.
 */
export function tenthsToText(tenths: number): string {
  return (tenths / 10).toFixed(1);
}

/**
 * A typed percentage back into tenths, or `null` when it is not a number.
 *
 * Rounded to one decimal place and **clamped to nothing**: §6.17 range-checks `0..1000` on the
 * server and `weight_out_of_range` is a refusal the user should see, not something the browser
 * silently fixes. Clamping here would hide a typo the server is about to name.
 */
export function textToTenths(text: string): number | null {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value)) return null;
  return Math.round(value * 10);
}

/** Whether a typed weight is inside the range the package's guards accept (§6.17's `0..1000`). */
export function isWeightInRange(tenths: number): boolean {
  return Number.isInteger(tenths) && tenths >= 0 && tenths <= 1000;
}
